import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, DollarSign, TrendingUp, FileText, Shield, Eye } from 'lucide-react';

const dimensions = [
  {
    num: '01', icon: BarChart3, label: 'Reportable',
    title: 'Structured for Reporting',
    subtitle: 'Financial Reporting',
    desc: 'Structured financial reporting that meets the standards of lenders, investors, and oversight bodies — timely, accurate, and audit-worthy. We design and operate reporting systems that satisfy institutional expectations.',
    points: ['GAAP-compliant reporting', 'Management reporting packages', 'Investor-ready statements', 'Real-time reporting visibility'],
  },
  {
    num: '02', icon: DollarSign, label: 'Fundable',
    title: 'Built to Attract Capital',
    subtitle: 'Capital Readiness',
    desc: 'Financial records, projections, and operational history that give capital providers the confidence to invest or extend credit. We prepare businesses to present compellingly to any funding source.',
    points: ['Investment-ready financials', 'Historical record organization', 'Projection development', 'Funding package preparation'],
  },
  {
    num: '03', icon: TrendingUp, label: 'Bankable',
    title: 'Meeting Commercial Bank Standards',
    subtitle: 'Banking Relationships',
    desc: 'Meeting the financial standards, controls, and documentation that commercial banks require for credit facilities and banking relationships. We help businesses earn and maintain banking confidence.',
    points: ['Bank package preparation', 'Credit facility documentation', 'Financial covenant compliance', 'Relationship maintenance'],
  },
  {
    num: '04', icon: FileText, label: 'Audit-Ready',
    title: 'Ready for Any Review',
    subtitle: 'Audit Preparation',
    desc: 'Financial statements, controls, and records organized and maintained to withstand professional audit and third-party review scrutiny at any time — not just at audit season.',
    points: ['Audit file organization', 'Internal control documentation', 'Year-round audit readiness', 'Third-party review support'],
  },
  {
    num: '05', icon: Shield, label: 'Controlled',
    title: 'Operationally Mature',
    subtitle: 'Internal Controls',
    desc: 'Internal controls, oversight structures, and accountability systems that demonstrate operational maturity to lenders, investors, and boards. We build the control environment that institutional investors expect.',
    points: ['Internal control design', 'Oversight structure implementation', 'Control documentation', 'Segregation of duties'],
  },
  {
    num: '06', icon: Eye, label: 'Visible',
    title: 'Full Financial Visibility',
    subtitle: 'Data Visibility',
    desc: 'Real-time and historical financial visibility that enables confident decision-making at every level of the organization — from daily operations to board-level strategy.',
    points: ['Real-time financial dashboards', 'Management reporting', 'KPI tracking & alerts', 'Historical trend analysis'],
  },
];

const partners = [
  'Private Equity & Venture Capital Funds',
  'Community Development Financial Institutions (CDFIs)',
  'Small Business Accelerators & Incubators',
  'Portfolio Companies & Growth-Stage Businesses',
  'Commercial Lenders & Banking Institutions',
  'Strategic Partners & M&A Advisors',
];

export default function CapitalPartners() {
  const [activeTab, setActiveTab] = useState(0);
  const dim = dimensions[activeTab];
  const DimIcon = dim.icon;

  return (
    <div>
      {/* ── Hero ─────────────────────────────────── */}
      <section className="grid-bg pt-32 pb-14" data-testid="capital-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-4">Portfolio Support & Capital Partners</p>
            <h1 className="text-5xl sm:text-6xl font-bold text-[#1A2234] leading-[1.05] tracking-tight mb-5 max-w-3xl">
              Helping Companies Become<br /><span className="text-[#0ABFBC]">Capital-Ready</span>
            </h1>
            <p className="text-lg text-[#475569] max-w-2xl leading-relaxed">
              Boston Federal partners with funds, lenders, accelerators, and portfolio organizations to help their companies achieve the financial infrastructure and operational maturity needed to access and retain capital.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Readiness Dimensions Tab Section ─────── */}
      <section className="bg-white pb-0" data-testid="dimensions-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="pt-14 mb-8">
            <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-2">The Readiness Framework</p>
            <h2 className="text-3xl font-bold text-[#1A2234]">Six Dimensions of Financial Readiness</h2>
          </div>

          {/* Tab Bar */}
          <div className="border-b border-slate-100">
            <div className="flex overflow-x-auto no-scrollbar">
              {dimensions.map((d, i) => (
                <button
                  key={d.label}
                  onClick={() => setActiveTab(i)}
                  data-testid={`dimension-tab-${i}`}
                  className={`px-5 py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors duration-200 ${
                    i === activeTab ? 'text-[#0ABFBC] border-[#0ABFBC]' : 'text-[#475569] border-transparent hover:text-[#1A2234]'
                  }`}
                >
                  <span className="text-[#0ABFBC]/60 text-xs mr-2 font-mono">{d.num}</span>{d.label}
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28 }}
              className="py-14 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center"
            >
              <div className="lg:col-span-3">
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-[#0ABFBC] font-bold text-3xl font-mono">{dim.num}</span>
                  <div className="w-10 h-10 rounded-lg bg-[#0ABFBC]/10 flex items-center justify-center">
                    <DimIcon size={19} strokeWidth={1.5} className="text-[#0ABFBC]" />
                  </div>
                </div>
                <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.2em] mb-2">{dim.subtitle}</p>
                <h3 className="text-3xl font-bold text-[#1A2234] mb-4 leading-tight">{dim.title}</h3>
                <p className="text-[#475569] leading-relaxed mb-7">{dim.desc}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {dim.points.map(p => (
                    <div key={p} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#0ABFBC] flex-shrink-0 mt-[7px]" />
                      <span className="text-[#475569] text-sm">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-2 flex justify-center">
                <div className="w-52 h-52 rounded-full bg-[#0ABFBC]/5 flex items-center justify-center">
                  <div className="w-36 h-36 rounded-full bg-[#0ABFBC]/8 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-[#0ABFBC]/12 flex items-center justify-center">
                      <DimIcon size={40} strokeWidth={0.9} className="text-[#0ABFBC]/60" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── Who We Work With ──────────────────────── */}
      <section className="bg-[#F8FAFC] py-20" data-testid="who-we-work-with">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">Who We Work With</p>
              <h2 className="text-3xl font-bold text-[#1A2234] leading-tight tracking-tight mb-5">Partners in Financial Readiness</h2>
              <p className="text-[#475569] leading-relaxed mb-7">
                Boston Federal works with a broad range of capital partners and portfolio organizations — any entity that needs to ensure their businesses have the financial infrastructure to grow, scale, and perform.
              </p>
              <div className="space-y-3.5">
                {partners.map(p => (
                  <div key={p} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#0ABFBC]/15 flex items-center justify-center flex-shrink-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#0ABFBC]" />
                    </div>
                    <span className="text-[#475569] text-sm">{p}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact" data-testid="capital-start-conversation" className="mt-8 bg-[#0ABFBC] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#09A09D] transition-colors inline-flex items-center gap-2">
                Start a Conversation <ArrowRight size={17} />
              </Link>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <div className="pt-2">
                <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-5">Capital Readiness Dimensions</p>
                <div className="flex flex-wrap gap-2">
                  {dimensions.map(d => (
                    <button
                      key={d.label}
                      onClick={() => { setActiveTab(dimensions.findIndex(x => x.label === d.label)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="rounded-full bg-[#0ABFBC]/10 text-[#09A09D] px-4 py-1.5 text-xs font-bold border border-[#0ABFBC]/20 hover:bg-[#0ABFBC]/20 transition-colors cursor-pointer"
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
                <p className="text-[#475569] text-sm leading-relaxed mt-6">
                  Boston Federal assesses, builds, and operationalizes all six dimensions — providing a clear, measurable path from financial fragility to capital confidence.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Dark CTA ──────────────────────────────── */}
      <section className="dark-grid-bg py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <h2 className="text-4xl font-bold text-white mb-4">Help Your Portfolio Become Capital-Ready</h2>
            <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">Partner with Boston Federal to build the financial infrastructure your portfolio companies need.</p>
            <Link to="/contact" data-testid="capital-cta" className="bg-[#0ABFBC] text-white px-8 py-3.5 rounded-md font-semibold hover:bg-[#09A09D] transition-colors inline-flex items-center gap-2">
              Talk to Our Team <ArrowRight size={17} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
